import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { financialsApi, usersApi, type PortfolioWipJob, type PortfolioWip } from '@/lib/apiClient';
import { StateView } from '@/components';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { formatPeriod } from '@/lib/wipHistory';
import { asOfQuery, closedFinalityNote, monthPortfolio } from '@/lib/portfolioMonth';
import { downloadMonthWorkbook } from '@/lib/portfolioExport';
import MonthClosePanel from './MonthClosePanel';
import GlStaleCheckPanel from '../sage/GlStaleCheckPanel';

// Portfolio WIP roll-up (Reports → Work-in-Progress). One row per active job,
// division-grouped with subtotals + a company total. Reads the same per-job WIP
// read model the Job Financials tab uses, rolled up server-side. Gated
// financials.view AND showProjectCosts (checked by the Reports shell).

const moneyC = (v: number) => {
  const a = Math.abs(v), sign = v < 0 ? '-' : '';
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3)}K`;
  return `${sign}$${Math.round(a)}`;
};
const pct0 = (v: number) => `${Math.round(v * 100)}%`;
const pct1 = (v: number) => `${(v * 100).toFixed(1)}%`;
const HEALTH: Record<string, string> = { green: '#027835', orange: '#DB7A25', red: '#B8321C' };

type Sort = 'division' | 'fade' | 'underbilled';

const css = `
  .pw-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:var(--ew-s-4); }
  .pw-kpis { display:grid; grid-template-columns:repeat(auto-fit,minmax(190px,1fr)); gap:var(--ew-s-3); margin-bottom:var(--ew-s-4); }
  .pw-kpi { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:14px 16px 14px 18px; position:relative; overflow:hidden; }
  .pw-kpi::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--acc,var(--ew-n-300)); }
  .pw-kpi .l { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); }
  .pw-kpi .v { font:var(--ew-fw-bold) 24px/1.1 var(--ew-font); font-variant-numeric:tabular-nums; color:var(--ew-dark); margin-top:6px; }
  .pw-kpi .s { font-size:12px; color:var(--ew-n-500); margin-top:4px; }
  .pw-pill { display:inline-flex; align-items:center; gap:4px; font:var(--ew-fw-semibold) 11px/1 var(--ew-font); border-radius:3px; padding:3px 7px; margin-top:6px; width:fit-content; }
  .pw-controls { display:flex; align-items:center; gap:14px; flex-wrap:wrap; margin-bottom:12px; }
  .pw-chips { display:flex; gap:6px; flex-wrap:wrap; }
  .pw-chip { border:1px solid var(--ew-n-300); background:#fff; color:var(--ew-n-600); border-radius:var(--ew-r-pill); padding:5px 12px; font:var(--ew-fw-medium) 12px/1 var(--ew-font); cursor:pointer; }
  .pw-chip.active { background:var(--ew-green-50); border-color:var(--ew-green); color:var(--ew-green-900); font-weight:600; }
  .pw-seg { display:inline-flex; gap:4px; }
  .pw-seg button { border:1px solid var(--ew-n-200); background:#fff; color:var(--ew-n-600); cursor:pointer; border-radius:var(--ew-r-md); padding:6px 12px; font:var(--ew-fw-medium) 12px/1 var(--ew-font); }
  .pw-seg button.active { background:var(--ew-dark); border-color:var(--ew-dark); color:#fff; }
  .pw-btn { font:var(--ew-fw-semibold) 12px/1 var(--ew-font); border-radius:var(--ew-r-md); padding:8px 14px; cursor:pointer; border:1px solid var(--ew-n-300); background:#fff; color:var(--ew-dark); }
  .pw-btn:disabled { opacity:.5; cursor:not-allowed; }
  .pw-tbl { width:100%; border-collapse:collapse; font-size:12.5px; }
  .pw-tbl th { text-align:right; padding:9px 10px; font:var(--ew-fw-semibold) 10.5px/1 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; color:var(--ew-n-500); border-bottom:1px solid var(--ew-n-100); white-space:nowrap; background:#fff; position:sticky; top:0; z-index:1; }
  .pw-tbl th:first-child, .pw-tbl td:first-child { text-align:left; position:sticky; left:0; background:#fff; z-index:2; }
  .pw-tbl th:first-child { z-index:3; }
  .pw-tbl td { padding:9px 10px; text-align:right; font-variant-numeric:tabular-nums; border-bottom:1px solid #F2F2F1; color:var(--ew-dark); white-space:nowrap; }
  .pw-grouphdr td { background:var(--ew-n-50); font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.05em; text-transform:uppercase; color:var(--ew-n-600); }
  .pw-sub td { font-weight:600; background:#FAFAF9; border-top:1px solid var(--ew-n-200); }
  .pw-tbl tfoot td { font-weight:700; border-top:2px solid var(--ew-n-300); background:#fff; }
  .pw-dot { display:inline-block; width:9px; height:9px; border-radius:50%; margin-right:8px; vertical-align:middle; }
  .pw-joblink { color:var(--ew-green-700); text-decoration:none; font-weight:600; }
  .pw-joblink:hover { text-decoration:underline; }
  .pw-section { margin-bottom:var(--ew-s-4); }
  .pw-section h3 { font:var(--ew-fw-semibold) 13px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-dark); margin:0 0 8px; }
  .pw-viewbar { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:var(--ew-s-4); }
  .pw-viewbar label { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); }
  .pw-viewbar select { font:var(--ew-fw-medium) 13px/1 var(--ew-font); padding:7px 10px; border:1px solid var(--ew-n-300); border-radius:var(--ew-r-md); background:#fff; color:var(--ew-dark); }
  .pw-pastbanner { background:#e7f0f8; border:1px solid #c9dff0; border-radius:var(--ew-r-md); padding:10px 14px; margin-bottom:var(--ew-s-4); font-size:12.5px; line-height:1.5; color:#15659E; }
  .pw-legend { display:flex; flex-wrap:wrap; gap:6px 18px; font-size:11.5px; color:#5A5A56; margin-top:12px; }
  .pw-legend span { display:inline-flex; align-items:center; gap:6px; }
`;

interface Agg {
  revised: number; billed: number; budget: number; actual: number; eac: number;
  earned: number; projectedProfit: number; indirectActual: number; superProfit: number; estimateProfit: number; fadeDollars: number; overUnderBilled: number; leftToBill: number;
  /** Contract of the jobs that HAVE an estimate: the base fade is measured against, so a job with
   *  none (a month view only) does not dilute the margin-vs-estimate figure. */
  estRevised: number;
}
const zero = (): Agg => ({ revised: 0, billed: 0, budget: 0, actual: 0, eac: 0, earned: 0, projectedProfit: 0, indirectActual: 0, superProfit: 0, estimateProfit: 0, fadeDollars: 0, overUnderBilled: 0, leftToBill: 0, estRevised: 0 });
const add = (a: Agg, j: PortfolioWipJob) => {
  a.estRevised += j.noEstimate ? 0 : j.revised;
  a.revised += j.revised; a.billed += j.billed; a.budget += j.budget; a.actual += j.actual; a.eac += j.eac;
  a.earned += j.earned; a.projectedProfit += j.projectedProfit; a.indirectActual += j.indirectActual; a.superProfit += j.superProfit; a.estimateProfit += j.estimateProfit;
  a.fadeDollars += j.fadeDollars; a.overUnderBilled += j.overUnderBilled; a.leftToBill += j.leftToBill;
};

export default function PortfolioWip() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60_000 });
  const q = useQuery({ queryKey: ['portfolio-wip'], queryFn: () => financialsApi.portfolio(), staleTime: 60_000 });
  const isAdmin = me?.roles.includes('Admin') ?? false;
  // Taking a snapshot writes a period marker across every job, so it needs
  // forecast-edit rights — not merely permission to read the roll-up. Mirrors
  // the API gate on POST /financials/portfolio/snapshot. A portfolio-only
  // viewer (exec/accounting) reads the page without the snapshot button.
  const canSnapshot = isAdmin || (me?.permissions.includes('financials.edit_forecast') ?? false);
  // Drill into a job's Financials tab only if the viewer also has per-job access
  // — a portfolio-only viewer (exec/accounting) sees the job number as text.
  const canViewJob = isAdmin || (me?.permissions.includes('financials.view') ?? false);
  // Closing a month is accounting's act, with its own permission (#613).
  const canClose = isAdmin || (me?.permissions.includes('financials.close_month') ?? false);
  // The stale-line check's API accepts Close access OR admin.access (it used to be admin-only and
  // lived in Admin > Integrations), so the panel is offered to the same people: an admin.access
  // role that cannot Close would otherwise be let in by the server and shown nothing here.
  const canCheckGl = canClose || (me?.permissions.includes('admin.access') ?? false);

  const [division, setDivision] = useState('All');
  const [sort, setSort] = useState<Sort>('division');
  const [snapMsg, setSnapMsg] = useState('');

  // A past month, '' = the live roll-up (#613). The month's jobs are the raw as-of models, and
  // their figures are computed here with the job tab's own calc (lib/portfolioMonth).
  const [month, setMonth] = useState('');
  const [exportError, setExportError] = useState('');
  const [exporting, setExporting] = useState(false);
  const periodsQ = useQuery({ queryKey: ['financials', 'periods'], queryFn: () => financialsApi.periods(), staleTime: 30_000 });
  const asOfQ = useQuery({
    queryKey: ['financials', 'portfolio-as-of', month],
    queryFn: () => financialsApi.portfolioAsOf(month),
    enabled: month !== '',
    staleTime: 60_000,
  });
  const monthView = useMemo(() => (asOfQ.data ? monthPortfolio(asOfQ.data) : null), [asOfQ.data]);
  const closedMonths = useMemo(
    () => new Set((periodsQ.data?.periods ?? []).filter((p) => p.closed).map((p) => p.period)),
    [periodsQ.data],
  );
  // StateView only reads the loading, error and data fields, and this page draws from `view`,
  // so the two queries' different data shapes do not matter to it.
  const activeQ: { isLoading: boolean; isPending: boolean; isError: boolean; error: unknown; data?: unknown } = month ? asOfQ : q;

  const snapMut = useMutation({
    mutationFn: () => financialsApi.snapshotPortfolio(),
    onSuccess: (r) => setSnapMsg(`✓ WIP snapshot taken for ${r.count} jobs`),
  });

  const view = useMemo(() => {
    const jobs = month ? (monthView?.jobs.map((m) => m.figures) ?? []) : (q.data?.jobs ?? []);
    const company = jobs.reduce((a, j) => (add(a, j), a), zero());
    const jobsFading = jobs.filter((j) => j.fadeDollars < 0).length;
    const divisions = ['All', ...Array.from(new Set(jobs.map((j) => j.division))).sort()];
    const filtered = division === 'All' ? jobs : jobs.filter((j) => j.division === division);

    // Build display groups. Division sort = grouped w/ subtotals; the flat sorts
    // put the worst first.
    type Group = { label: string | null; rows: PortfolioWipJob[]; sub: Agg };
    let groups: Group[];
    if (sort === 'division') {
      const byDiv = new Map<string, PortfolioWipJob[]>();
      for (const j of filtered) { const k = j.division || '—'; (byDiv.get(k) ?? byDiv.set(k, []).get(k)!).push(j); }
      groups = Array.from(byDiv.keys()).sort().map((label) => {
        const rows = byDiv.get(label)!.slice().sort((a, b) => a.jobNumber.localeCompare(b.jobNumber));
        const sub = rows.reduce((a, j) => (add(a, j), a), zero());
        return { label, rows, sub };
      });
    } else {
      const rows = filtered.slice().sort(sort === 'fade'
        ? (a, b) => a.fadeDollars - b.fadeDollars
        : (a, b) => a.overUnderBilled - b.overUnderBilled);
      groups = [{ label: null, rows, sub: rows.reduce((a, j) => (add(a, j), a), zero()) }];
    }
    // Contracts outside the tracker, for the reconciliation panel. Passed
    // through untouched — it is not affected by the division filter or sort,
    // since it describes what the table CANNOT show.
    // Live contracts, not a past month's: the panel stays out of the month view.
    const unreconciled = month ? undefined : q.data?.unreconciled;
    return { jobs, company, jobsFading, divisions, filtered, groups, unreconciled };
  }, [q.data, monthView, month, division, sort]);

  const blendedMargin = view.company.revised ? view.company.projectedProfit / view.company.revised : 0;
  // Points of margin lost to fade: fade dollars over the contract of the jobs that have an
  // estimate. For the live roll-up (every job has one) this is (blended margin - blended estimate
  // margin), as it always was; in a month view a job with no estimate adds nothing to either part.
  const fadePtsBlended = view.company.estRevised ? (view.company.fadeDollars / view.company.estRevised) * 100 : 0;
  const wipUnder = view.company.overUnderBilled < 0;

  return (
    <div>
      <style>{css}</style>
      <div className="pw-head">
        <div>
          <h2 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-h3)/var(--ew-lh-snug) var(--ew-font)', color: 'var(--ew-dark)' }}>Work-in-Progress</h2>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--ew-fs-body-sm)', color: 'var(--ew-n-500)' }}>
            <span style={{ color: '#8A4E12', background: '#FBEEE1', borderRadius: 3, padding: '2px 7px', fontSize: 11, fontWeight: 600 }}>🔒 Finance &amp; PM only</span>
            <span style={{ color: 'var(--ew-quality)', fontWeight: 600, marginLeft: 8 }}>◇ Synced from Sage Intacct</span>
            {!month && q.data?.provenance.syncedAt && <span style={{ marginLeft: 8 }}>Last synced {fmtDate(q.data.provenance.syncedAt)}</span>}
          </p>
        </div>
        {canSnapshot && !month && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {snapMsg && <span style={{ fontSize: 12, color: 'var(--ew-green-700)', fontWeight: 600 }}>{snapMsg}</span>}
            <button className="pw-btn" onClick={() => snapMut.mutate()} disabled={snapMut.isPending}>{snapMut.isPending ? 'Taking snapshot…' : '📸 Take WIP Snapshot'}</button>
          </div>
        )}
      </div>

      {canClose && <MonthClosePanel />}

      {/* The check for stale GL lines (#613) is accounting's review before a close, so it sits with
          Close and has the same gate: the controller runs it without being given Admin. */}
      {canCheckGl && (
        <section className="pw-section">
          <h3>Cost lines Intacct no longer has</h3>
          <GlStaleCheckPanel />
        </section>
      )}

      {/* View: the live roll-up, or every job as of the end of a past month (read-only). */}
      <div className="pw-viewbar">
        <label htmlFor="pw-month">View</label>
        <select id="pw-month" value={month} onChange={(e) => { setMonth(e.target.value); setDivision('All'); setExportError(''); }}>
          <option value="">Current (live)</option>
          {(periodsQ.data?.periods ?? []).map((p) => (
            <option key={p.period} value={p.period}>{formatPeriod(p.period)} — {closedMonths.has(p.period) ? 'closed' : 'month end'}</option>
          ))}
        </select>
        {month && asOfQ.data && monthView && (
          <button className="pw-btn" disabled={exporting} onClick={async () => {
            // A failure building or saving the file must say so: otherwise the button just does nothing.
            // The Accruals sheet is read when the button is pressed. If it cannot be read there is NO file:
            // a workbook that quietly lacks the accruals would look complete to the person booking the journal.
            setExporting(true);
            try {
              const accruals = await financialsApi.portfolioAccruals(month);
              downloadMonthWorkbook(asOfQ.data!, monthView, accruals);
              setExportError('');
            } catch (e) { setExportError(`Could not make the file: ${e instanceof Error ? e.message : 'unknown error'}`); }
            finally { setExporting(false); }
          }}>{exporting ? 'Making the file…' : 'Export to Excel'}</button>
        )}
        {exportError && <span role="alert" style={{ fontSize: 12, color: '#B8321C', fontWeight: 600 }}>{exportError}</span>}
      </div>

      {month && asOfQ.data && (() => {
        const d = asOfQ.data;
        const left = d.skipped.length + (monthView?.noContract.length ?? 0);
        // Counted over the jobs the table shows, so the sentence matches what is on screen.
        const shown = monthView?.jobs ?? [];
        const recut = d.closed ? shown.filter((j) => !j.final).length : 0;
        const noEst = shown.filter((j) => j.noEstimate).length;
        const gone = shown.filter((j) => j.deleted).length;
        return (
          <div className="pw-pastbanner" role="note">
            {d.closed ? (
              <>
                <b>{formatPeriod(d.period)} — closed{d.closed.by ? ` by ${d.closed.by}` : ''} on {fmtDateTime(d.closed.at)}. Read only.</b>{' '}
                {closedFinalityNote(shown.length, recut)}
                {' '}Cost is every entry dated through {fmtDate(d.through)}; the contract, estimate and purchase orders are as they stood at month end.
              </>
            ) : (
              <>
                <b>{formatPeriod(d.period)} — month end. Read only.</b>{' '}
                Cost is every entry dated through {fmtDate(d.through)} as Intacct has it now, so entries posted after month end but dated inside it are included, and the figures can still change until accounting closes the month.
                The contract, estimate and purchase orders are as the month-end freeze recorded them; the forecast is the PM&rsquo;s as of then.
              </>
            )}
            {gone > 0 && <> {gone} job{gone === 1 ? ' has' : 's have'} been deleted from the Project Tracker since this month and {gone === 1 ? 'is' : 'are'} still counted in it, marked "deleted since".</>}
            {noEst > 0 && <> {noEst} job{noEst === 1 ? ' had' : 's had'} no estimate synced at month end: {noEst === 1 ? 'its' : 'their'} fade and health are unknown, shown as — and a grey dot, and {noEst === 1 ? 'it is' : 'they are'} left out of the fade totals. Contract, cost and billing are unaffected.</>}
            {left > 0 && <> {left} job{left === 1 ? ' is' : 's are'} not in this view; the export lists {left === 1 ? 'it' : 'them'} with the reason.</>}
          </div>
        );
      })()}

      <StateView query={activeQ} empty="No jobs with contract data yet.">
        {() => {
          const { company, jobsFading, jobs, divisions, groups } = view;
          return (
            <>
              {/* Company KPI band — always the FULL portfolio (unfiltered). */}
              <div className="pw-kpis">
                <div className="pw-kpi" style={{ ['--acc' as any]: 'var(--ew-dark)' }}>
                  <div className="l">Total Contract</div><div className="v">{moneyC(company.revised)}</div>
                  <div className="s">{jobs.length} active job{jobs.length === 1 ? '' : 's'}</div>
                </div>
                <div className="pw-kpi" style={{ ['--acc' as any]: 'var(--ew-green)' }}>
                  <div className="l">Backlog · Left to Bill</div><div className="v">{moneyC(company.leftToBill)}</div>
                  <div className="s">of {moneyC(company.revised)} contract</div>
                </div>
                <div className="pw-kpi" style={{ ['--acc' as any]: 'var(--ew-performance)' }}>
                  <div className="l">Blended Margin</div>
                  <div className="v">{moneyC(company.projectedProfit)} <span style={{ fontSize: 14, color: 'var(--ew-n-500)' }}>{pct1(blendedMargin)}</span></div>
                  <span className="pw-pill" style={{ background: company.fadeDollars < 0 ? '#FBEEE1' : 'var(--ew-green-50)', color: company.fadeDollars < 0 ? '#8A4E12' : 'var(--ew-green-700)' }}>
                    {company.fadeDollars < 0 ? '▼' : '▲'} {moneyC(company.fadeDollars)} · {fadePtsBlended.toFixed(1)} pts
                  </span>
                </div>
                <div className="pw-kpi" style={{ ['--acc' as any]: 'var(--ew-quality)' }}>
                  <div className="l">WIP Position</div>
                  <div className="v">{moneyC(Math.abs(company.overUnderBilled))} <span style={{ fontSize: 13, color: 'var(--ew-quality)', fontWeight: 600 }}>{wipUnder ? 'Underbilled' : 'Overbilled'}</span></div>
                  <div className="s">Billed {moneyC(company.billed)} · Earned {moneyC(company.earned)}</div>
                </div>
                <div className="pw-kpi" style={{ ['--acc' as any]: jobsFading > 0 ? 'var(--ew-danger)' : 'var(--ew-green)' }}>
                  <div className="l">Jobs Fading</div><div className="v" style={{ color: jobsFading > 0 ? '#B8321C' : 'var(--ew-green-700)' }}>{jobsFading} <span style={{ fontSize: 15, color: 'var(--ew-n-500)' }}>of {jobs.length}</span></div>
                  <div className="s">projected margin below estimate</div>
                </div>
              </div>

              {/* Controls */}
              <div className="pw-controls">
                <div className="pw-chips">
                  {divisions.map((d) => (
                    <button key={d} className={`pw-chip${division === d ? ' active' : ''}`} onClick={() => setDivision(d)}>{d}</button>
                  ))}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ew-n-500)', fontWeight: 600 }}>Sort</span>
                  <div className="pw-seg">
                    <button className={sort === 'division' ? 'active' : ''} onClick={() => setSort('division')}>Division</button>
                    <button className={sort === 'fade' ? 'active' : ''} onClick={() => setSort('fade')}>Worst Fade</button>
                    <button className={sort === 'underbilled' ? 'active' : ''} onClick={() => setSort('underbilled')}>Underbilled</button>
                  </div>
                </div>
              </div>

              {/* WIP table */}
              <div className="ew-table-scroll" style={{ maxHeight: '65vh', overflow: 'auto' }}>
                <table className="pw-tbl">
                  <thead>
                    <tr>
                      <th>Job</th><th>Contract</th><th>% Comp</th><th>Proj Cost</th><th>Cost to Date ◇</th>
                      <th>Proj Profit</th><th>Indirects ◇</th><th>Super Margin</th><th>Fade</th><th>Billed</th><th>Earned</th><th>Over/(Under)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((grp) => (
                      <GroupBlock key={grp.label ?? 'flat'} grp={grp} showHeader={sort === 'division'} canViewJob={canViewJob} month={month} />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Company total{division !== 'All' ? ` · ${division}` : ''}</td>
                      {(() => {
                        const c = view.filtered.reduce((a, j) => (add(a, j), a), zero());
                        return (
                          <>
                            <td>{moneyC(c.revised)}</td>
                            <td>{pct0(c.eac ? c.actual / c.eac : 0)}</td>
                            <td>{moneyC(c.eac)}</td>
                            <td>{moneyC(c.actual)}</td>
                            <td>{moneyC(c.projectedProfit)}</td>
                            <td style={{ color: 'var(--ew-n-500)' }}>{c.indirectActual ? moneyC(c.indirectActual) : '—'}</td>
                            <td style={{ color: 'var(--ew-green-700)' }}>{moneyC(c.superProfit)} <span style={{ color: 'var(--ew-n-400)', fontSize: 11 }}>{pct0(c.revised ? c.superProfit / c.revised : 0)}</span></td>
                            <td style={{ color: c.fadeDollars < 0 ? '#B5611A' : 'var(--ew-green-700)' }}>{moneyC(c.fadeDollars)}</td>
                            <td>{moneyC(c.billed)}</td>
                            <td>{moneyC(c.earned)}</td>
                            <td style={{ color: c.overUnderBilled < 0 ? '#B5611A' : 'var(--ew-quality)' }}>{c.overUnderBilled < 0 ? `(${moneyC(-c.overUnderBilled)})` : moneyC(c.overUnderBilled)}</td>
                          </>
                        );
                      })()}
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="pw-legend">
                <span><span className="pw-dot" style={{ background: HEALTH.green, marginRight: 0 }} /> On plan</span>
                <span><span className="pw-dot" style={{ background: HEALTH.orange, marginRight: 0 }} /> Margin fading</span>
                <span><span className="pw-dot" style={{ background: HEALTH.red, marginRight: 0 }} /> Fading &amp; underbilled</span>
                <span><span style={{ color: '#B5611A', fontWeight: 700 }}>⚠</span> Estimate exceeds contract — reconcile in Sage (baseline issue, not an overrun)</span>
                <span><span style={{ color: 'var(--ew-n-500)', fontWeight: 700 }}>◇</span> Actual cost from the GL feed{q.data && !q.data.provenance.actualEnabled ? ' (illustrative until enabled)' : ''}</span>
                <span><b>Indirects</b> = indirect cost allocated to the job in the GL (account 59998) — the gap between the two margins</span>
                <span><b style={{ color: 'var(--ew-green-700)' }}>Super Margin</b> = projected margin before job-allocated indirects (GL 59998)</span>
              </div>

              <UnreconciledPanel data={view.unreconciled} />
            </>
          );
        }}
      </StateView>
    </div>
  );
}

/**
 * Sage contracts with no project in the tracker.
 *
 * The WIP table only shows tracked jobs, so it will never tie to Sage's total.
 * This explains the difference and surfaces genuine omissions — deliberately as
 * a panel rather than table rows, because the great majority are finished,
 * fully-billed jobs that correctly don't belong in a work-in-progress report
 * and would bury the live ones.
 *
 * The headline totals cover EVERY contract outside the tracker; the list is only
 * those with an unbilled balance, since those are the financially live ones.
 */
function UnreconciledPanel({ data }: { data: PortfolioWip['unreconciled'] | undefined }) {
  const [open, setOpen] = useState(false);
  if (!data || data.contracts === 0) return null;

  const finished = data.contracts - data.openContracts;
  const likelyOmissions = data.jobs.filter((j) => j.recentCost);

  return (
    <div style={{ marginTop: 18, border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', background: 'var(--ew-white)' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
      >
        <span style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ew-dark)' }}>
          {data.contracts} contracted job{data.contracts === 1 ? '' : 's'} outside the tracker
        </span>
        <span style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
          {moneyC(data.revised)} contracted · {moneyC(data.leftToBill)} still to bill
        </span>
        {likelyOmissions.length > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#8A4E12', background: '#FBEEE1', border: '1px solid #F0D9C0', borderRadius: 'var(--ew-r-pill)', padding: '2px 9px' }}>
            ⚠ {likelyOmissions.length} still costing
          </span>
        )}
      </button>

      {open && (
        <div style={{ padding: '0 14px 14px' }}>
          <p style={{ fontSize: 12, color: 'var(--ew-n-500)', lineHeight: 1.55, margin: '0 0 12px' }}>
            These have a Sage contract but no project in the tracker, so the table above can’t
            include them — which is why this page won’t tie exactly to Sage.{' '}
            <b>{finished} of them are fully billed</b> and finished, which is correct for a
            work-in-progress report. Listed below are the {data.openContracts} with an unbilled
            balance. Anything marked <b style={{ color: '#8A4E12' }}>still costing</b> has had cost
            posted in the last {data.recentDays} days — that usually means a live job that ought to
            be added to the tracker rather than history.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="pw-tbl" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Job</th><th>Name</th><th>Customer</th><th>Contract</th>
                  <th>Left to Bill</th><th>Last Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.jobs.map((j) => (
                  <tr key={j.jobNumber} style={{ background: j.recentCost ? '#FEFAF4' : undefined }}>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {j.jobNumber}
                      {j.recentCost && (
                        <span title={`Cost posted in the last ${data.recentDays} days — likely belongs in the tracker`} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#8A4E12' }}>
                          ⚠
                        </span>
                      )}
                    </td>
                    <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.name || '—'}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ew-n-500)' }}>{j.customerName || '—'}</td>
                    <td>{moneyC(j.revised)}</td>
                    <td style={{ fontWeight: 600 }}>{moneyC(j.leftToBill)}</td>
                    <td style={{ color: 'var(--ew-n-500)', whiteSpace: 'nowrap' }}>{j.lastCost ? fmtDate(j.lastCost) : 'never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function GroupBlock({ grp, showHeader, canViewJob, month }: { grp: { label: string | null; rows: PortfolioWipJob[]; sub: Agg }; showHeader: boolean; canViewJob: boolean; month: string }) {
  return (
    <>
      {showHeader && grp.label && (
        <tr className="pw-grouphdr"><td colSpan={12}>{grp.label} · {grp.rows.length} job{grp.rows.length === 1 ? '' : 's'}</td></tr>
      )}
      {grp.rows.map((j) => (
        <tr key={j.projectId}>
          <td>
            <span className="pw-dot" style={{ background: j.noEstimate ? 'var(--ew-n-300)' : HEALTH[j.health] }} title={j.noEstimate ? 'No estimate synced at month end: health unknown' : j.health} />
            {canViewJob && !j.deletedSince
              ? <Link className="pw-joblink" to={`/projects/${j.projectId}?tab=financials${month ? asOfQuery(month) : ''}`}>{j.jobNumber}</Link>
              : <span style={{ fontWeight: 600, color: 'var(--ew-dark)' }}>{j.jobNumber}</span>}
            {j.revised > 0 && j.budget > j.revised && (
              <span title={`Estimate exceeds contract by ${moneyC(j.budget - j.revised)} — a baseline issue to reconcile in Sage, not a cost overrun`} style={{ marginLeft: 6, color: '#B5611A', cursor: 'help', fontWeight: 700 }}>⚠</span>
            )}
            <span style={{ color: 'var(--ew-n-500)', marginLeft: 6 }}>{j.title}</span>
            {j.deletedSince && (
              <span title="This project has been deleted since this month. It is still counted in it, because the month is what was frozen." style={{ marginLeft: 6, color: '#8A4E12', background: '#FBEEE1', borderRadius: 3, padding: '1px 6px', fontSize: 10.5, fontWeight: 600 }}>deleted since</span>
            )}
          </td>
          <td>{moneyC(j.revised)}</td>
          <td>{pct0(j.pctComplete)}</td>
          <td>{moneyC(j.eac)}</td>
          <td style={{ color: 'var(--ew-n-500)' }}>{moneyC(j.actual)}</td>
          <td>{moneyC(j.projectedProfit)} <span style={{ color: 'var(--ew-n-400)', fontSize: 11 }}>{pct0(j.projectedMarginPct)}</span></td>
          {/* Indirect burden on its own column — the gap between the two margins. */}
          <td style={{ color: j.indirectActual ? 'var(--ew-n-500)' : 'var(--ew-n-300)' }}>{j.indirectActual ? moneyC(j.indirectActual) : '—'}</td>
          <td style={{ color: 'var(--ew-green-700)' }}>{moneyC(j.superProfit)} <span style={{ color: 'var(--ew-n-400)', fontSize: 11 }}>{pct0(j.superMarginPct)}</span></td>
          {j.noEstimate
            ? <td style={{ color: 'var(--ew-n-300)' }} title="No estimate synced at month end, so there is nothing to fade against">—</td>
            : <td style={{ color: j.fadeDollars < 0 ? '#B5611A' : 'var(--ew-green-700)' }}>{moneyC(j.fadeDollars)}</td>}
          <td>{moneyC(j.billed)}</td>
          <td>{moneyC(j.earned)}</td>
          <td style={{ color: j.overUnderBilled < 0 ? '#B5611A' : 'var(--ew-quality)' }}>{j.overUnderBilled < 0 ? `(${moneyC(-j.overUnderBilled)})` : moneyC(j.overUnderBilled)}</td>
        </tr>
      ))}
      {showHeader && (
        <tr className="pw-sub">
          <td>{grp.label} subtotal</td>
          <td>{moneyC(grp.sub.revised)}</td>
          <td>{pct0(grp.sub.eac ? grp.sub.actual / grp.sub.eac : 0)}</td>
          <td>{moneyC(grp.sub.eac)}</td>
          <td>{moneyC(grp.sub.actual)}</td>
          <td>{moneyC(grp.sub.projectedProfit)}</td>
          <td style={{ color: 'var(--ew-n-500)' }}>{grp.sub.indirectActual ? moneyC(grp.sub.indirectActual) : '—'}</td>
          <td style={{ color: 'var(--ew-green-700)' }}>{moneyC(grp.sub.superProfit)} <span style={{ color: 'var(--ew-n-400)', fontSize: 11 }}>{pct0(grp.sub.revised ? grp.sub.superProfit / grp.sub.revised : 0)}</span></td>
          <td style={{ color: grp.sub.fadeDollars < 0 ? '#B5611A' : 'var(--ew-green-700)' }}>{moneyC(grp.sub.fadeDollars)}</td>
          <td>{moneyC(grp.sub.billed)}</td>
          <td>{moneyC(grp.sub.earned)}</td>
          <td style={{ color: grp.sub.overUnderBilled < 0 ? '#B5611A' : 'var(--ew-quality)' }}>{grp.sub.overUnderBilled < 0 ? `(${moneyC(-grp.sub.overUnderBilled)})` : moneyC(grp.sub.overUnderBilled)}</td>
        </tr>
      )}
    </>
  );
}
